// Analyser health helpers for gating the transport based on actual audio output

/** Quick RMS probe in dBFS from an AnalyserNode's time-domain data. */
export function probeRmsDb(an: AnalyserNode): number | null {
  try {
    const size = an.fftSize || 2048;
    const buf = new Float32Array(size);
    (an as any).getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length) || 1e-12;
    return 20 * Math.log10(rms);
  } catch {
    return null;
  }
}

/**
 * Poll analyser until signal exceeds threshold, then open the transport gate.
 * Useful after scheduling/reschedules to avoid staying muted if audio arrives late.
 */
export function openGateWhenSignal(
  refs: any,
  {
    thresholdDb = -85,        // tolerate quiet intros
    timeoutMs = 4000,
    intervalMs = 120,
    rampMs = 80,
    onTimeout,
  }: { thresholdDb?: number; timeoutMs?: number; intervalMs?: number; rampMs?: number; onTimeout?: () => void } = {}
) {
  try { const { L } = require('../../../../utils/logger'); L.info('media:gateprobe', 'entered'); } catch {}
  // Capture the current post-probe id to detect staleness; newer scopes increment this.
  const postProbeId = (() => { try { return (refs as any)._postProbeId ?? 0; } catch { return 0; } })();

  // Guard with ownership: allow a newer probe to supersede an older one
  try {
    const active = (refs as any)._gateProbeActive;
    const owner = (refs as any)._gateProbeOwnerId;
    if (!active) {
      (refs as any)._gateProbeActive = true;
      (refs as any)._gateProbeOwnerId = postProbeId;
    } else if (owner !== postProbeId) {
      // Supersede: start a new loop; the old one will bail on id mismatch at next tick
      (refs as any)._gateProbeActive = true;
      (refs as any)._gateProbeOwnerId = postProbeId;
    } else {
      // Already running for this id
      return;
    }
  } catch {}

  // Prefer pre-gate analyser so we can see signal even when the gate is closed
  const an: AnalyserNode | null = (refs?.masterAnalyserPreRef?.current ?? refs?.masterAnalyserRef?.current) ?? null;
  if (!an) {
    try { (refs as any)._gateProbeActive = false; } catch {}
    return;
  }

  // Lazy require to avoid import cycles
  const { makeTransportGate } = require('./transportGate');
  const { openTransportGate } = makeTransportGate(refs);
  const { tagNode } = require('./diag');
  const { L } = require('../../../../utils/logger');
  const gateGid = tagNode(refs?.gateGainRef?.current ?? refs?.masterGainRef?.current);
  try { L.info('media:gateprobe', 'start', { gateGid, thresholdDb, timeoutMs }); } catch {}

  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const clearGuard = () => { try { (refs as any)._gateProbeActive = false; (refs as any)._gateProbeOwnerId = undefined; } catch {}; };
  const tick = () => {
    // Bail quickly if this probe has been superseded by a newer scope
    try { if (((refs as any)._postProbeId ?? 0) !== postProbeId) { clearGuard(); return; } } catch {}
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (now - t0 > timeoutMs) {
      // Timeout: release guard before escalation to avoid concurrent resets
      clearGuard();
      try { L.warn('media:gateprobe', 'timeout', { gateGid, lastDb: (refs as any)._gateProbeLastDb ?? null }); } catch {}
      try { onTimeout?.(); } catch {}
      return;
    }
    // Safety: if analyser is tied to a different or closed AudioContext, abort and release guard
    try {
      const ctxNow: AudioContext | null = (refs?.audioCtxRef?.current ?? null) as any;
      const anCtx: AudioContext | null = (an?.context as any) ?? null;
      const anClosed = (anCtx && typeof (anCtx as any).state === 'string' && (anCtx as any).state === 'closed');
      const ctxMismatch = (ctxNow && anCtx && ctxNow !== anCtx);
      if (anClosed || ctxMismatch) { clearGuard(); return; }
    } catch {}
    let db: number | null = null;
    try {
      const size = an.fftSize || 2048;
      const buf = new Float32Array(size);
      (an as any).getFloatTimeDomainData(buf);
      let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length) || 1e-12;
      db = 20 * Math.log10(rms);
    } catch {}
    try { (refs as any)._gateProbeLastDb = db; } catch {}
    if (db != null && db > thresholdDb) {
      try { (refs as any)._silentStarts = 0; } catch {}
      try { openTransportGate(rampMs); } catch {}
      try { L.info('media:gateprobe', 'open', { gateGid, db }); } catch {}
      // Success: release guard
      clearGuard();
    } else {
      // light telemetry (throttled by intervalMs)
      try {
        const lastTs = (refs as any)._gateProbeLastLogTs || 0;
        if ((now - lastTs) > 800) {
          (refs as any)._gateProbeLastLogTs = now;
          L.info('media:gateprobe', 'tick', { db });
        }
      } catch {}
      setTimeout(tick, intervalMs);
    }
  };
  tick();
}
